import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import { ApiManager } from '../../../../../core/api-manager';
import { LoginManager } from '../../../../../core/login-manager';
import { generateId } from '../../utils/constants';
import './style.css';

export default function QueryPanel({
  queries = [],
  onQueriesChange,
  selectedQuery,
  onSelectQuery,
  onQueryDataLoaded,
  previewData = {},
  onPreviewDataChange
}) {
  // State
  const [entityFields, setEntityFields] = useState({});
  const [loading, setLoading] = useState(false);

  // Current query editing state
  const [queryName, setQueryName] = useState('');
  const [queryDescription, setQueryDescription] = useState('');
  const [sqlQuery, setSqlQuery] = useState('');
  const [queryError, setQueryError] = useState(null);

  // Preview state
  const [localPreviewData, setLocalPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Autocomplete state
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [cursorPosition, setCursorPosition] = useState(0);

  // Modal state
  const [showEditorModal, setShowEditorModal] = useState(false);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  const textareaRef = useRef(null);
  const modalTextareaRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Load entity fields on mount
  useEffect(() => {
    loadEntityFields();
  }, []);

  // Update form when selectedQuery changes
  useEffect(() => {
    if (selectedQuery) {
      setQueryName(selectedQuery.name || '');
      setQueryDescription(selectedQuery.description || '');
      setSqlQuery(selectedQuery.sql_query || selectedQuery.sql || '');
      // Load preview for selected query
      const sql = selectedQuery.sql_query || selectedQuery.sql;
      if (sql) {
        executePreview(sql);
      }
    }
  }, [selectedQuery]);

  const loadEntityFields = async () => {
    try {
      const response = await ApiManager.sendAuthenticatedRequest('/utils/crystal_get_entity_fields', {});
      if (response.success === 1) {
        setEntityFields(response.body || {});
      }
    } catch (error) {
      console.error('Error loading entity fields:', error);
    }
  };

  // Build autocomplete suggestions
  const buildSuggestions = useCallback((text, cursorPos) => {
    if (!text || Object.keys(entityFields).length === 0) {
      return [];
    }

    // Get the current word being typed
    const beforeCursor = text.substring(0, cursorPos);
    const match = beforeCursor.match(/[\w.]*$/);
    const currentWord = match ? match[0].toLowerCase() : '';

    if (currentWord.length < 2) {
      return [];
    }

    const results = [];

    // Check if we're typing after a table name (e.g., "user.")
    const dotIndex = currentWord.lastIndexOf('.');
    if (dotIndex > 0) {
      const tableName = currentWord.substring(0, dotIndex);
      const fieldPrefix = currentWord.substring(dotIndex + 1);

      // Find matching table
      const table = Object.keys(entityFields).find(t => t.toLowerCase() === tableName);
      if (table && entityFields[table]?.fields) {
        entityFields[table].fields.forEach(field => {
          if (field.name.toLowerCase().startsWith(fieldPrefix)) {
            results.push({
              type: 'field',
              value: field.name,
              display: `${table}.${field.name}`,
              description: `${field.type}`,
              replaceFrom: cursorPos - currentWord.length
            });
          }
        });
      }
    } else {
      // Search for tables and fields
      Object.entries(entityFields).forEach(([tableName, tableInfo]) => {
        // Match table names
        if (tableName.toLowerCase().startsWith(currentWord)) {
          results.push({
            type: 'table',
            value: tableName,
            display: tableName,
            description: 'tabella',
            replaceFrom: cursorPos - currentWord.length
          });
        }

        // Match field names
        if (tableInfo.fields) {
          tableInfo.fields.forEach(field => {
            if (field.name.toLowerCase().startsWith(currentWord)) {
              results.push({
                type: 'field',
                value: `${tableName}.${field.name}`,
                display: `${tableName}.${field.name}`,
                description: `${field.type}`,
                replaceFrom: cursorPos - currentWord.length
              });
            }
          });
        }
      });
    }

    // Limit results
    return results.slice(0, 10);
  }, [entityFields]);

  // Handle textarea change
  const handleSqlChange = (e) => {
    const newValue = e.target.value;
    const newCursor = e.target.selectionStart;

    setSqlQuery(newValue);
    setCursorPosition(newCursor);
    setQueryError(null);

    // Build suggestions
    const newSuggestions = buildSuggestions(newValue, newCursor);
    setSuggestions(newSuggestions);
    setShowSuggestions(newSuggestions.length > 0);
    setSuggestionIndex(0);
  };

  // Handle keyboard navigation in suggestions
  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSuggestionIndex(prev => Math.min(prev + 1, suggestions.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSuggestionIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Tab':
      case 'Enter':
        if (showSuggestions && suggestions[suggestionIndex]) {
          e.preventDefault();
          applySuggestion(suggestions[suggestionIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        break;
    }
  };

  // Apply selected suggestion
  const applySuggestion = (suggestion) => {
    const before = sqlQuery.substring(0, suggestion.replaceFrom);
    const after = sqlQuery.substring(cursorPosition);
    const newValue = before + suggestion.value + after;

    setSqlQuery(newValue);
    setShowSuggestions(false);

    // Focus and set cursor position
    if (modalTextareaRef.current) {
      const newPos = suggestion.replaceFrom + suggestion.value.length;
      modalTextareaRef.current.focus();
      setTimeout(() => {
        modalTextareaRef.current.setSelectionRange(newPos, newPos);
      }, 0);
    }
  };

  // Execute query preview
  const executePreview = async (sql = sqlQuery) => {
    if (!sql.trim()) {
      setQueryError('Inserisci una query SQL');
      return;
    }

    setPreviewLoading(true);
    setQueryError(null);

    try {
      const response = await ApiManager.sendAuthenticatedRequest('/utils/crystal_execute_query', {
        sql: sql,
        preview: true,
        limit: 10
      });

      if (response.success === 1) {
        setLocalPreviewData(response.body);
        // Notify parent of loaded data
        if (onQueryDataLoaded) {
          onQueryDataLoaded(response.body);
        }
      } else {
        setQueryError(response.message || 'Errore nell\'esecuzione della query');
        setLocalPreviewData(null);
      }
    } catch (error) {
      setQueryError('Errore di connessione');
      setLocalPreviewData(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Test query validity
  const testQuery = async () => {
    if (!sqlQuery.trim()) {
      setQueryError('Inserisci una query SQL');
      return;
    }

    setLoading(true);
    setQueryError(null);

    try {
      const response = await ApiManager.sendAuthenticatedRequest('/utils/crystal_test_query', {
        sql: sqlQuery
      });

      if (response.success === 1) {
        setQueryError(null);
        toast.success('Query valida!');
      } else {
        setQueryError(response.message || 'Query non valida');
      }
    } catch (error) {
      setQueryError('Errore di connessione');
    } finally {
      setLoading(false);
    }
  };

  // Save query locally
  const saveQuery = async () => {
    if (!queryName.trim()) {
      toast.warning('Inserisci un nome per la query');
      return;
    }

    if (!sqlQuery.trim()) {
      toast.warning('Inserisci una query SQL');
      return;
    }

    // Test the query first
    setLoading(true);
    try {
      const response = await ApiManager.sendAuthenticatedRequest('/utils/crystal_test_query', {
        sql: sqlQuery
      });

      if (response.success !== 1) {
        setQueryError(response.message || 'Query non valida');
        setLoading(false);
        return;
      }
    } catch (error) {
      setQueryError('Errore di connessione');
      setLoading(false);
      return;
    }

    // Save locally with author and timestamp
    const userInfo = LoginManager.getUserInfo();
    const now = new Date().toISOString();

    const newQuery = {
      id: selectedQuery?.id || generateId(),
      name: queryName,
      description: queryDescription,
      sql_query: sqlQuery,
      author: userInfo?.name || 'Unknown',
      createdAt: selectedQuery?.createdAt || now,
      updatedAt: now
    };

    let updatedQueries;
    if (selectedQuery?.id) {
      // Update existing
      updatedQueries = queries.map(q => q.id === selectedQuery.id ? newQuery : q);
    } else {
      // Add new
      updatedQueries = [...queries, newQuery];
    }

    onQueriesChange(updatedQueries);
    onSelectQuery(newQuery);
    setShowEditorModal(false);
    toast.success(selectedQuery?.id ? 'Query aggiornata' : 'Query salvata');
    setLoading(false);

    // Execute preview to update data
    executePreview(sqlQuery);
  };

  // Delete query locally
  const deleteQuery = (queryId) => {
    const updatedQueries = queries.filter(q => q.id !== queryId);
    onQueriesChange(updatedQueries);

    if (selectedQuery?.id === queryId) {
      resetForm();
    }

    // Remove from preview data
    if (previewData[queryId]) {
      const newPreviewData = { ...previewData };
      delete newPreviewData[queryId];
      onPreviewDataChange(newPreviewData);
    }

    toast.success('Query eliminata');
  };

  // Confirm delete with toast
  const confirmDeleteQuery = (queryId, queryName) => {
    toast.warn(
      <div>
        <p style={{ margin: '0 0 10px 0' }}>Eliminare la query "{queryName}"?</p>
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
          <button
            onClick={() => toast.dismiss()}
            style={{ padding: '4px 12px', border: '1px solid #ccc', borderRadius: '4px', background: '#fff', cursor: 'pointer' }}
          >
            Annulla
          </button>
          <button
            onClick={() => { toast.dismiss(); deleteQuery(queryId); }}
            style={{ padding: '4px 12px', border: 'none', borderRadius: '4px', background: '#dc3545', color: '#fff', cursor: 'pointer' }}
          >
            Elimina
          </button>
        </div>
      </div>,
      { autoClose: false, closeButton: false }
    );
  };

  // Reset form for new query
  const resetForm = () => {
    setQueryName('');
    setQueryDescription('');
    setSqlQuery('');
    setQueryError(null);
    setLocalPreviewData(null);
    onSelectQuery(null);
  };

  // Open modal for new query
  const openNewQueryModal = () => {
    resetForm();
    setShowEditorModal(true);
  };

  // Open modal for editing existing query
  const openEditQueryModal = (query) => {
    onSelectQuery(query);
    setShowEditorModal(true);
  };

  // Handle query selection from list
  const handleSelectQuery = (query) => {
    onSelectQuery(query);
  };

  // Filter queries based on search term
  const filteredQueries = useMemo(() => {
    if (!searchTerm.trim()) return queries;
    const term = searchTerm.toLowerCase();
    return queries.filter(q =>
      q.name?.toLowerCase().includes(term) ||
      q.description?.toLowerCase().includes(term)
    );
  }, [queries, searchTerm]);

  // Handle column drag start
  const handleColumnDragStart = (e, column) => {
    const dragData = {
      type: 'query_column',
      column: column,
      queryId: selectedQuery?.id,
      queryName: selectedQuery?.name,
      isMultiRow: localPreviewData?.isMultiRow || false
    };
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.effectAllowed = 'copy';
  };

  return (
    <div className="query-panel">
      {/* Query List Section */}
      <div className="query-section">
        <div className="section-header">
          <span className="section-title">
            <FontAwesomeIcon icon="database" /> Query Locali
          </span>
          <button className="btn-new-query" onClick={openNewQueryModal} title="Nuova Query">
            <FontAwesomeIcon icon="plus" />
          </button>
        </div>

        {/* Search bar */}
        {queries.length > 0 && (
          <div className="query-search">
            <FontAwesomeIcon icon="search" className="search-icon" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca query..."
              className="search-input"
            />
            {searchTerm && (
              <button className="search-clear" onClick={() => setSearchTerm('')}>
                <FontAwesomeIcon icon="times" />
              </button>
            )}
          </div>
        )}

        <div className="query-list">
          {queries.length === 0 ? (
            <div className="empty-list">
              <FontAwesomeIcon icon="info-circle" />
              <span>Nessuna query</span>
              <small style={{ display: 'block', marginTop: '4px', opacity: 0.7 }}>
                Le query vengono salvate nel file del report
              </small>
            </div>
          ) : filteredQueries.length === 0 ? (
            <div className="empty-list">
              <FontAwesomeIcon icon="search" />
              <span>Nessun risultato per "{searchTerm}"</span>
            </div>
          ) : (
            filteredQueries.map(query => (
              <div
                key={query.id}
                className={`query-list-item ${selectedQuery?.id === query.id ? 'selected' : ''}`}
                onClick={() => handleSelectQuery(query)}
              >
                <div className="query-item-info">
                  <div className="query-item-name">{query.name}</div>
                  <div className="query-item-meta">
                    {query.author && <span>{query.author}</span>}
                    {query.updatedAt && (
                      <span> - {new Date(query.updatedAt).toLocaleDateString('it-IT', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}</span>
                    )}
                  </div>
                </div>
                <div className="query-item-actions">
                  <button
                    className="query-item-btn edit"
                    onClick={(e) => { e.stopPropagation(); openEditQueryModal(query); }}
                    title="Modifica"
                  >
                    <FontAwesomeIcon icon="pen" />
                  </button>
                  <button
                    className="query-item-btn delete"
                    onClick={(e) => { e.stopPropagation(); confirmDeleteQuery(query.id, query.name); }}
                    title="Elimina"
                  >
                    <FontAwesomeIcon icon="trash" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Preview Section */}
      {localPreviewData && (
        <div className="query-section preview-section">
          <div className="section-header">
            <span className="section-title">
              <FontAwesomeIcon icon="table" /> Anteprima
              <span className="row-count">({localPreviewData.rowCount} righe{localPreviewData.isMultiRow ? ' - multi' : ' - singola'})</span>
            </span>
          </div>

          <div className="preview-info">
            <FontAwesomeIcon icon="info-circle" />
            <span>Trascina le colonne nel layout per aggiungerle</span>
          </div>

          {/* Draggable columns */}
          <div className="preview-columns">
            {localPreviewData.columns.map((col, index) => (
              <div
                key={index}
                className="preview-column"
                draggable
                onDragStart={(e) => handleColumnDragStart(e, col)}
              >
                <FontAwesomeIcon icon="grip-vertical" className="drag-handle" />
                <span className="column-name">{col.name}</span>
              </div>
            ))}
          </div>

          {/* Data preview table */}
          {localPreviewData.data.length > 0 && (
            <div className="preview-table-wrapper">
              <table className="preview-table">
                <thead>
                  <tr>
                    {localPreviewData.columns.map((col, i) => (
                      <th key={i}>{col.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {localPreviewData.data.slice(0, 5).map((row, rowIndex) => (
                    <tr key={rowIndex}>
                      {localPreviewData.columns.map((col, colIndex) => (
                        <td key={colIndex}>
                          {row[col.name] !== null ? String(row[col.name]).substring(0, 50) : '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {localPreviewData.data.length > 5 && (
                <div className="preview-more">
                  ... e altre {localPreviewData.data.length - 5} righe
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Query Editor Modal */}
      {showEditorModal && (
        <div className="query-modal-overlay" onClick={() => setShowEditorModal(false)}>
          <div className="query-modal" onClick={e => e.stopPropagation()}>
            <div className="query-modal-header">
              <h3>
                <FontAwesomeIcon icon="code" />
                {selectedQuery ? 'Modifica Query' : 'Nuova Query'}
              </h3>
              <button className="modal-close-btn" onClick={() => setShowEditorModal(false)}>
                <FontAwesomeIcon icon="times" />
              </button>
            </div>

            <div className="query-modal-body">
              <div className="modal-form-row">
                <label>Nome:</label>
                <input
                  type="text"
                  value={queryName}
                  onChange={(e) => setQueryName(e.target.value)}
                  placeholder="Nome della query..."
                  className="query-input"
                />
              </div>

              <div className="modal-form-row">
                <label>Descrizione:</label>
                <input
                  type="text"
                  value={queryDescription}
                  onChange={(e) => setQueryDescription(e.target.value)}
                  placeholder="Descrizione opzionale..."
                  className="query-input"
                />
              </div>

              <div className="modal-form-row sql-row-modal">
                <label>Query SQL:</label>
                <div className="sql-editor-wrapper-modal">
                  <textarea
                    ref={modalTextareaRef}
                    value={sqlQuery}
                    onChange={handleSqlChange}
                    onKeyDown={handleKeyDown}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder="SELECT * FROM tabella WHERE deleted = 0"
                    className="sql-textarea-modal"
                    spellCheck={false}
                  />

                  {/* Autocomplete suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="sql-suggestions" ref={suggestionsRef}>
                      {suggestions.map((sugg, index) => (
                        <div
                          key={index}
                          className={`suggestion-item ${index === suggestionIndex ? 'selected' : ''}`}
                          onClick={() => applySuggestion(sugg)}
                        >
                          <span className={`suggestion-type ${sugg.type}`}>
                            {sugg.type === 'table' ? 'T' : 'F'}
                          </span>
                          <span className="suggestion-value">{sugg.display}</span>
                          <span className="suggestion-desc">{sugg.description}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {queryError && (
                <div className="query-error">
                  <FontAwesomeIcon icon="exclamation-triangle" />
                  {queryError}
                </div>
              )}
            </div>

            <div className="query-modal-footer">
              <button className="btn-query btn-test" onClick={testQuery} disabled={loading}>
                <FontAwesomeIcon icon="check-circle" /> Testa
              </button>
              <button className="btn-query btn-preview" onClick={() => executePreview()} disabled={previewLoading}>
                <FontAwesomeIcon icon={previewLoading ? 'spinner' : 'play'} spin={previewLoading} /> Esegui
              </button>
              <div className="modal-footer-spacer"></div>
              <button className="btn-query btn-cancel" onClick={() => setShowEditorModal(false)}>
                Annulla
              </button>
              <button className="btn-query btn-save" onClick={saveQuery} disabled={loading}>
                <FontAwesomeIcon icon="save" /> Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
